/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAccusedAdvocate,
  fetchAdminUnitDetail,
} from "services/advocateComplaintService";
import {
  AccusedAdvocate,
  AdminUnit,
  AdvocateComplaints,
  Complaints,
  OFFENCE_TYPE_FOR_ADVOCATE,
} from "types";

import ConfirmationModal from "components/ConfirmationModal";

import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { addAdvocateComplaint } from "services/advocateComplaintService";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";

const formatDate = (date: any) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const subtractYears = (date: any, years: number) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() - years);
  return d;
};

const offenseWindowYearsForAdvocate = (
  offenceType: string | undefined | null,
) => {
  const k = (offenceType || "").toLowerCase();
  if (k.includes("grave")) return 4;
  if (k.includes("medium")) return 3;
  return 2; // petty or others
};

const NewAdvocateComplaint: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});

  const contextData = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [isAccusationReportByMobile, setIsAccusationReportByMobile] =
    useState(false);
  const [formState, setFormState] = useState<AdvocateComplaints>({
    uu_id: 0,
    receiving_prosecutor: null,
    assigned_prosecutor: "",
    reporters: [],
    victims: [],
    witnesses: [],
    accused_advocate: null,
    accused_prosecutor_name: "",
    accused_prosecutor_phone: "",
    accused_license_number: "",
    reception_date: null,
    sex: "",
    offence_type: "",
    offence_description: "",
    decision_type: null,
    decision_summary: "",
    assigned_date: null,
    status: COMPLIANT_STATUS?.PENDING,
    accused_geneder: "Male",
    accused_phone_number: "",
    commission_of_disciplinary_offense_date: null,
  });

  const [accusedProsecutorsData, setAccusedProsecutorsData] = useState<
    AccusedAdvocate[]
  >([]);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);

  // Close dropdown when clicking outside

  useEffect(() => {
    setLoading(true);
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!query) return;

    const loadAccusedProsecutors = async () => {
      setLoading(true);
      try {
        const data = await fetchAccusedAdvocate(query);
        setAccusedProsecutorsData(data);
        setError("");

        // Show dropdown if we have results and query is not empty
        if (query.trim() && data?.length > 0) {
          setShowDropdown(true);
        }
      } catch (err) {
        setError(query ? t("failedToLoadAdvocates") : "");
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to prevent too many API calls
    const timer = setTimeout(() => {
      loadAccusedProsecutors();
    }, 300);

    return () => clearTimeout(timer);
  }, [query, t]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAccusedProsecutorSelect = (
    accused: AccusedAdvocate,
    name: string,
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: accused,
      accused_license_number: accused?.license_no || "",
      accused_phone_number: accused?.phone,
      accused_geneder: accused?.gender,
      accused_advocate_admin_unit_id: accused?.admin_unit,
    }));
    setQuery("");
    setShowDropdown(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim()) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setAccusedProsecutorsData([]);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: boolean } = {};

    if (!formState.reception_date) {
      errors.reception_date = true;
      setInvalidFields(errors);
      return t("receptionDateRequired");
    }

    if (!formState.commission_of_disciplinary_offense_date) {
      errors.commission_of_disciplinary_offense_date = true;
      setInvalidFields(errors);
      return t("offenseDateRequired");
    }

    if (!formState.accused_advocate) {
      setInvalidFields({ ...errors, accused_advocate: true });
      return t("accusedAdvocateRequired");
    }

    const offenseDate = new Date(
      formState.commission_of_disciplinary_offense_date,
    );
    const receptionDate = new Date(formState.reception_date);
    const yearDiff =
      (receptionDate.getTime() - offenseDate.getTime()) /
      (1000 * 3600 * 24 * 365.25);

    const offenseType = formState.offence_type?.toLowerCase();

    if (offenseType.includes("grave") && yearDiff > 4) {
      errors.reception_date = true;
      errors.commission_of_disciplinary_offense_date = true;
      setInvalidFields(errors);
      return t("graveOffenseError");
    }

    if (offenseType.includes("medium") && yearDiff > 3) {
      errors.reception_date = true;
      errors.commission_of_disciplinary_offense_date = true;
      setInvalidFields(errors);
      return t("mediumOffenseError");
    }

    if (offenseType.includes("petty") && yearDiff > 2) {
      errors.reception_date = true;
      errors.commission_of_disciplinary_offense_date = true;
      setInvalidFields(errors);
      return t("pettyOffenseError");
    }

    setInvalidFields({});
    return "";
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = validateForm();

    if (error) {
      showToast(error, "error");
      return;
    }

    if (!showConfirmationModal) {
      setShowConfirmationModal(true);
      return;
    }
  };

  const handleConfirm = async () => {
    setSubmitLoading(true);

    try {
      const resData = (await addAdvocateComplaint({
        ...formState,
        user_id_receiving_prosecutor: user?.user_id,
        receiving_prosecutor: user?.full_name,
        admin_unit_id: user?.admin_unit,
        org_id: user?.org_unit,
        accused_name:
          typeof formState.accused_advocate === "object" &&
          formState.accused_advocate !== null
            ? (formState.accused_advocate.name ?? "")
            : "",
        userid_accused_name:
          typeof formState.accused_advocate === "object" &&
          formState.accused_advocate !== null
            ? formState.accused_advocate.id
            : undefined,

        is_regional:
          adminUnitData?.hierarchy_level?.name === "Regional" ? true : false,
        is_zonal:
          adminUnitData?.hierarchy_level?.name === "Regional" ? false : true,
      })) as Complaints;

      navigate(`/advocate-complaint/${resData?.uu_id}`);
      showToast(t("successfullyCreated"), "success");
      setShowConfirmationModal(false);
      setFormState({
        uu_id: 0,
        receiving_prosecutor: null,
        assigned_prosecutor: "",
        reporters: [],
        victims: [],
        witnesses: [],
        accused_advocate: null,
        accused_prosecutor_name: "",
        accused_prosecutor_phone: "",
        reception_date: null,
        sex: "",
        offence_type: "",
        offence_description: "",
        decision_type: null,
        decision_summary: "",
        assigned_date: null,
        status: COMPLIANT_STATUS?.PENDING,
        accused_geneder: "Male",
        accused_phone_number: "",
        accused_license_number: "",
        commission_of_disciplinary_offense_date: null,
      });
      setQuery("");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Official Header */}
        <div className="mb-6 border-b-2 border-dashed border-gray-300 pb-8">
          <div className="flex items-start gap-6 mb-6">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-red-600 to-red-700 flex items-center justify-center shadow-lg">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-800 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">⚖️</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-0.5 bg-red-800"></div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t("newAdvocateComplaintRegistration")}
              </h1>
              <p className="text-gray-600 max-w-2xl">
                {t("officialFormDescription")}{" "}
                <span className="text-red-800 font-bold">*</span>{" "}
                {t("areMandatory")}.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Advocate Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300">
            <div className="border-b border-gray-300">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-8 bg-gradient-to-b from-red-800 to-red-900 rounded-r"></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      <span className="text-red-800">I.</span>{" "}
                      {t("advocateInformation")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("advocateInformationDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6" ref={dropdownRef}>
                <div className="border border-gray-400 p-5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-300">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-800 to-red-900 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {t("accusedAdvocate")}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t("accusedAdvocateDescription")}
                      </p>
                    </div>
                  </div>

                  {/* NEW: Mobile Report Checkbox and Conditional Application Number */}
                  <div className="mb-5 space-y-4">
                    <div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAccusationReportByMobile || false}
                          onChange={(e) =>
                            setIsAccusationReportByMobile(e.target.checked)
                          }
                          className="w-5 h-5 text-red-800 border-2 border-gray-400 rounded focus:ring-red-800 focus:ring-2 transition"
                        />
                        <span className="text-sm font-semibold text-gray-700">
                          {t("isAccusationReportByMobile")}
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-8">
                        {t("accusationReportByMobileDescription")}
                      </p>
                    </div>

                    {isAccusationReportByMobile && (
                      <div>
                        <label className="block mb-3">
                          <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                            {t("reportApplicationNumber")}
                          </span>
                          <span className="text-xs text-gray-500 block mb-2">
                            {t("reportApplicationNumberDescription")}
                          </span>
                        </label>
                        <input
                          type="text"
                          value={formState?.report_application_number || ""}
                          onChange={(e) =>
                            setFormState((prev) => ({
                              ...prev,
                              report_application_number: e.target.value,
                            }))
                          }
                          placeholder={t("reportApplicationNumberPlaceholder")}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                        />
                      </div>
                    )}
                  </div>

                  {/* NEW: Complaint Type Toggle (Advocate / Paralegal) */}
                  <div className="mb-5">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      {t("complaintType")}
                    </p>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formState?.is_on_advocate_complaint ?? true}
                          onChange={() =>
                            setFormState((prev) => ({
                              ...prev,
                              is_on_advocate_complaint: true,
                              is_on_paralegal_complaint: false,
                            }))
                          }
                          className="w-4 h-4 text-red-800 border-2 border-gray-400 rounded focus:ring-red-800"
                        />
                        <span className="text-sm text-gray-800">
                          {t("advocateComplaint")}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            formState?.is_on_paralegal_complaint ?? false
                          }
                          onChange={() =>
                            setFormState((prev) => ({
                              ...prev,
                              is_on_advocate_complaint: false,
                              is_on_paralegal_complaint: true,
                            }))
                          }
                          className="w-4 h-4 text-red-800 border-2 border-gray-400 rounded focus:ring-red-800"
                        />
                        <span className="text-sm text-gray-800">
                          {t("paralegalComplaint")}
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("complaintTypeDescription")}
                    </p>
                  </div>

                  <div className="space-y-5">
                    {/* Search Section */}
                    <div className="relative">
                      <label className="block mb-3">
                        <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                          {t("searchAdvocate")}
                          <span className="text-red-800 font-bold ml-1">*</span>
                        </span>
                        <span className="text-xs text-gray-500 block mb-2">
                          {t("searchAdvocateDescription")}
                        </span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={query}
                          onChange={handleSearchChange}
                          onFocus={() => {
                            if (
                              query.trim() &&
                              accusedProsecutorsData.length > 0
                            ) {
                              setShowDropdown(true);
                            }
                          }}
                          placeholder={t("searchAdvocatePlaceholder")}
                          className={`w-full pl-12 pr-10 py-3 bg-white border-2 rounded-lg text-gray-800 focus:outline-none focus:ring-1 transition-all ${
                            invalidFields.accused_advocate
                              ? "border-red-800 focus:border-red-800 focus:ring-red-200"
                              : "border-gray-400 focus:border-red-800 focus:ring-red-800"
                          }`}
                        />
                        {loading && (
                          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                            <div className="w-5 h-5 border-2 border-gray-400 border-t-red-800 rounded-full animate-spin"></div>
                          </div>
                        )}
                        {!loading && query && (
                          <button
                            type="button"
                            onClick={() => {
                              setQuery("");
                              setAccusedProsecutorsData([]);
                              setShowDropdown(false);
                              searchInputRef.current?.focus();
                            }}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-800"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Loading State */}
                      {loading && (
                        <div className="mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm text-red-800">
                              {t("searchingAdvocateRegistry")}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Error State */}
                      {error && (
                        <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <svg
                              className="w-5 h-5 text-red-800 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-sm text-red-800">
                              {error}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Dropdown Results */}
                      {showDropdown &&
                        query.trim() &&
                        accusedProsecutorsData.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-400 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {accusedProsecutorsData?.map(
                              (p: AccusedAdvocate) => (
                                <div
                                  key={p.id}
                                  className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-300 last:border-b-0 transition-colors"
                                  onClick={() => {
                                    handleAccusedProsecutorSelect(
                                      p,
                                      "accused_advocate",
                                    );
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-center text-white text-xs font-bold">
                                        {p.name?.[0] || "A"}
                                      </div>
                                      <div>
                                        <div className="font-semibold text-gray-900">
                                          {p.name || "-"}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                                          <span className="bg-gray-200 px-2 py-0.5 rounded">
                                            {t("license")}:{" "}
                                            {p.license_no || t("na")}
                                          </span>
                                          <span>{p.gender || t("na")}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <svg
                                      className="w-5 h-5 text-red-800"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}

                      {/* No Results Message */}
                      {showDropdown &&
                        query.trim() &&
                        !loading &&
                        accusedProsecutorsData.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-400 rounded-lg shadow-xl p-4">
                            <div className="flex items-center gap-3 text-gray-600">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>
                                {t("noAdvocatesFound")} "{query}"
                              </span>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Selected Advocate Card */}
                    {formState?.accused_advocate && (
                      <div className="bg-gradient-to-r from-gray-50 to-white border-2 border-red-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-center text-white font-bold text-lg">
                                {typeof formState?.accused_advocate ===
                                  "object" &&
                                formState?.accused_advocate !== null
                                  ? formState.accused_advocate.name?.[0] || "A"
                                  : "A"}
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-800 rounded-full border-2 border-white flex items-center justify-center">
                                <span className="text-xs text-white">✓</span>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 text-lg">
                                {typeof formState?.accused_advocate ===
                                  "object" &&
                                formState?.accused_advocate !== null
                                  ? formState.accused_advocate.name || "-"
                                  : "-"}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {t("legalAdvocate")}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormState((prev) => ({
                                ...prev,
                                accused_advocate: null,
                                accused_license_number: "",
                                accused_phone_number: "",
                                accused_geneder: "Male",
                              }));
                              setQuery("");
                              searchInputRef.current?.focus();
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <svg
                              className="w-5 h-5 text-gray-500 hover:text-red-800"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* Advocate Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div className="bg-white p-3 rounded border border-gray-300">
                            <div className="text-xs text-gray-500 mb-1">
                              {t("licenseNumber")}
                            </div>
                            <div className="font-medium text-gray-800">
                              {formState.accused_license_number || t("na")}
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded border border-gray-300">
                            <div className="text-xs text-gray-500 mb-1">
                              {t("phone")}
                            </div>
                            <div className="font-medium text-gray-800">
                              {formState.accused_phone_number || t("na")}
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded border border-gray-300">
                            <div className="text-xs text-gray-500 mb-1">
                              {t("gender")}
                            </div>
                            <div className="font-medium text-gray-800">
                              {formState.accused_geneder || t("na")}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Case Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300">
            <div className="border-b border-gray-300">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-8 bg-gradient-to-b from-red-800 to-red-900 rounded-r"></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      <span className="text-red-800">II.</span>{" "}
                      {t("caseDetails")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("caseDetailsDescription")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">
                  {t("basicInformation")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        {t("disciplinaryOffenseType")}
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 appearance-none transition-all"
                      name="offence_type"
                      value={formState.offence_type}
                      onChange={handleChange}
                    >
                      <option value="" className="text-gray-400">
                        {t("selectClassification")}
                      </option>
                      {Object.entries(OFFENCE_TYPE_FOR_ADVOCATE).map(
                        ([key, value]) => (
                          <option
                            key={key}
                            value={key}
                            className="text-gray-800"
                          >
                            ⚖️ {value}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        {t("complaintReceptionDate")}
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                    </label>
                    <input
                      required
                      className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-gray-800 focus:outline-none focus:ring-1 transition-all ${
                        invalidFields.reception_date
                          ? "border-red-800 focus:border-red-800 focus:ring-red-200"
                          : "border-gray-400 focus:border-red-800 focus:ring-red-800"
                      }`}
                      name="reception_date"
                      type="date"
                      max={formatDate(new Date())}
                      value={formState.reception_date?.toString() ?? ""}
                      onChange={handleChange}
                    />
                    {invalidFields.reception_date && (
                      <p className="text-xs text-red-800 mt-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t("fieldRequired")}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        {t("dateOfAllegedOffense")}
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                    </label>
                    <input
                      required
                      className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-gray-800 focus:outline-none focus:ring-1 transition-all ${
                        invalidFields.commission_of_disciplinary_offense_date
                          ? "border-red-800 focus:border-red-800 focus:ring-red-200"
                          : "border-gray-400 focus:border-red-800 focus:ring-red-800"
                      }`}
                      name="commission_of_disciplinary_offense_date"
                      type="date"
                      min={
                        formState?.reception_date
                          ? formatDate(
                              subtractYears(
                                formState.reception_date,
                                offenseWindowYearsForAdvocate(
                                  formState.offence_type as string,
                                ),
                              ),
                            )
                          : ""
                      }
                      max={
                        formState?.reception_date
                          ? formatDate(formState?.reception_date)
                          : ""
                      }
                      value={
                        formState.commission_of_disciplinary_offense_date?.toString() ??
                        ""
                      }
                      onChange={handleChange}
                    />
                    {invalidFields.commission_of_disciplinary_offense_date && (
                      <p className="text-xs text-red-800 mt-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t("fieldRequired")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Offense Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">
                  {t("detailedOffenseDescription")}
                </h3>
                <div className="rounded-lg p-1 bg-gradient-to-b from-white to-gray-50">
                  <div className="bg-white rounded-lg p-5">
                    <div className="relative">
                      <textarea
                        rows={6}
                        name="offence_description"
                        value={formState.offence_description}
                        onChange={handleChange}
                        placeholder={t("offenseDescriptionPlaceholder")}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 resize-none transition-all font-sans"
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-300">
                        {t("characters")}:{" "}
                        {formState.offence_description.length}/2000
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300 p-6">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-500 transition-all font-semibold flex items-center gap-3"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                {t("returnToDashboard")}
              </button>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      uu_id: 0,
                      receiving_prosecutor: null,
                      assigned_prosecutor: "",
                      reporters: [],
                      victims: [],
                      witnesses: [],
                      accused_advocate: null,
                      accused_prosecutor_name: "",
                      accused_prosecutor_phone: "",
                      reception_date: null,
                      sex: "",
                      offence_type: "",
                      offence_description: "",
                      decision_type: null,
                      decision_summary: "",
                      assigned_date: null,
                      status: COMPLIANT_STATUS?.PENDING,
                      accused_geneder: "Male",
                      accused_phone_number: "",
                      accused_license_number: "",
                      commission_of_disciplinary_offense_date: null,
                    });
                    setQuery("");
                  }}
                  className="px-8 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-500 transition-all font-semibold"
                >
                  {t("clearAllFields")}
                </button>
                <button
                  className="px-10 py-3 bg-gradient-to-r from-red-800 to-red-900 text-white rounded-lg hover:from-red-900 hover:to-red-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold shadow-lg hover:shadow-xl flex items-center gap-3 group"
                  type="submit"
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("processingSubmission")}
                    </>
                  ) : (
                    <>
                      <div className="relative">
                        <svg
                          className="w-6 h-6 group-hover:scale-110 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                      </div>
                      <span className="text-lg">
                        {t("submitAdvocateComplaint")}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={submitLoading}
      />
    </div>
  );
};

export default NewAdvocateComplaint;
